/**
 * One-click colour PREVIEW from a product card (image swap).
 *
 * The colour/flavour swatches on a card used to add the picked variant straight
 * to the cart. Clients preferred the swatch to behave like a variant picker: a
 * click now swaps the card's product image to that colour, moves the "selected"
 * halo, and points the card link at `?variant=` so clicking through opens that
 * colour's PDP. The blue quick-add bag button still adds the shown variant.
 *
 * One handler serves ALL three card renderers, which emit identical swatch markup
 * so the paths can't drift:
 *   - the Liquid card (snippets/curaprox-card-swatches.liquid)
 *   - the client-side Meilisearch listing (assets/meilisearch-collection.js)
 *   - the client-side Meilisearch search (assets/meilisearch-core.js)
 *
 * Image swap — two mechanisms, tried in order:
 *   1. Horizon slideshow: the Liquid card renders a <slideshow-component> whose
 *      slides carry `slide-id` = media.id, and the swatch carries `data-media-id`.
 *      `slideshow.select({ id })` reveals + shows that slide (the same call
 *      product-card.js uses for its native variant picker). Preferred: it keeps
 *      the gallery's own state coherent.
 *   2. Direct <img> src swap to `data-image`: the Meilisearch cards render
 *      positional slide-ids (slide-0/1/2), so their swatches can't address a
 *      media-id — and a Liquid card's target media may be beyond the gallery's
 *      rendered-slide cap. Swapping the visible slide image's src always works.
 */
(() => {
  const SWATCH_SELECTOR = '[data-cpc-swatch-add]';
  const MORE_SELECTOR = '[data-cpc-swatch-more]';
  const CARD_SELECTOR = 'product-card, .product-card, .cpc-card, li';

  /**
   * Marks the clicked swatch as the selected one within its row (moves the halo).
   * @param {HTMLElement} swatch - The clicked swatch.
   */
  function markSelected(swatch) {
    const row = swatch.closest('.cpc-swatches');
    if (!row) return;

    for (const other of row.querySelectorAll(SWATCH_SELECTOR)) {
      other.setAttribute('aria-pressed', String(other === swatch));
    }
  }

  /**
   * Points every card link (card, gallery, title) at the picked variant so that
   * clicking through opens the previewed colour's PDP, not the card default.
   * @param {Element} card - The product card.
   * @param {string} url - The variant URL (…/products/handle?variant=ID).
   */
  function updateCardLinks(card, url) {
    if (!url) return;
    for (const link of card.querySelectorAll('a[ref="productCardLink"], a[ref="cardGalleryLink"], a[ref="productTitleLink"], a.product-card__link')) {
      if (link instanceof HTMLAnchorElement) link.href = url;
    }
  }

  /**
   * Swaps the card's visible gallery image to the swatch's variant image.
   *
   * `data-image` (the colour's OVI main image, the same URL the PDP shows) is the
   * authority when present — swapped directly onto the visible base <img> so the
   * base and the OVI hover image stay from the same colour. Only when there is no
   * image URL do we fall back to Horizon's slideshow `select({ id })` by media-id
   * (a Shopify variant media that IS a rendered slide).
   * @param {Element} card - The product card.
   * @param {string | undefined} mediaId - The swatch's `data-media-id`.
   * @param {string | undefined} imageUrl - The swatch's `data-image`.
   * @returns {boolean} Whether an image swap could be applied.
   */
  function previewImage(card, mediaId, imageUrl) {
    // 1. Direct src swap to the OVI image on the visible slide's base image.
    //    Prefer the visible slide; fall back to the first base slide, then any
    //    gallery <img> that is NOT the hover overlay (swapping the overlay would
    //    show the wrong image on hover-out).
    if (imageUrl) {
      const baseImg =
        card.querySelector('slideshow-slide[aria-hidden="false"] img') ||
        card.querySelector('slideshow-slide img') ||
        card.querySelector('.card-gallery img:not(.card-gallery__hover-image)');
      if (baseImg instanceof HTMLImageElement) {
        baseImg.src = imageUrl;
        // Drop the responsive srcset so the browser can't re-pick the old image.
        baseImg.srcset = '';
        return true;
      }
    }

    // 2. No image URL — fall back to selecting the variant's Shopify media slide.
    const slideshow = card.querySelector('slideshow-component');
    if (slideshow && mediaId) {
      const slide = slideshow.querySelector(`slideshow-slide[slide-id="${CSS.escape(mediaId)}"]`);
      if (slide && typeof slideshow.select === 'function') {
        slideshow.select({ id: mediaId }, undefined, { animate: false });
        return true;
      }
    }

    return false;
  }

  /**
   * Points the card's hover (second) image at the picked colour's second image —
   * the card equivalent of the PDP showing that colour's full image set. The card
   * has one hover slot (`.card-gallery__hover-image`), fed by card-gallery.liquid
   * from the product-wide `option_value_images.__general.hover`; this repoints it
   * at THIS colour's image so hovering after a pick shows the right colour.
   *
   * Creates the overlay if the product had no hover image at render time. When the
   * swatch carries no hover URL at all (a single-image product with no OVI data),
   * the card's existing default overlay is left untouched — never removed — so a
   * colour pick can't blank a hover the card already had.
   * @param {Element} card - The product card.
   * @param {string | undefined} hoverUrl - The swatch's `data-hover-image`.
   * @param {string | undefined} mainUrl - The swatch's `data-image`, used when the colour has no second image.
   */
  function previewHoverImage(card, hoverUrl, mainUrl) {
    const gallery = card.querySelector('.card-gallery');
    if (!gallery) return;

    // No second image for this colour — the hover must still not flip the card
    // to the product-wide hover (another colour's image, Jan Krammer 2026-08-21:
    // "when I move the cursor to the Add to Cart button the image changes").
    // Point the overlay at the picked image instead, so hovering is a no-op
    // for this pick; the next pick with a real second image replaces it again.
    if (!hoverUrl) hoverUrl = mainUrl;
    if (!hoverUrl) return;

    let overlay = gallery.querySelector('.card-gallery__hover-image');

    if (!(overlay instanceof HTMLImageElement)) {
      overlay = document.createElement('img');
      overlay.className = 'card-gallery__hover-image';
      overlay.setAttribute('alt', '');
      overlay.setAttribute('role', 'presentation');
      overlay.setAttribute('aria-hidden', 'true');
      overlay.loading = 'lazy';
      // Match card-gallery.liquid: overlay sits inside .card-gallery, painted over
      // the base image by its own CSS (opacity 0 until :hover).
      gallery.appendChild(overlay);
      // Cards with no render-time hover image use Horizon's native pointer hover
      // (advances the slideshow). Now that an overlay drives the hover crossfade,
      // drop those handlers so the slideshow doesn't also shuffle underneath.
      gallery.removeAttribute('on:pointerenter');
      gallery.removeAttribute('on:pointerleave');
    }

    overlay.src = hoverUrl;
    overlay.srcset = '';
  }

  /**
   * Handles a swatch activation: preview the colour on the card.
   * @param {HTMLElement} swatch - The activated swatch.
   */
  function activateSwatch(swatch) {
    // aria-disabled marks sold-out variants and PIM "Kaufbarkeit"-blocked ones
    // (tasks 40921/45812) — they render dimmed and aren't previewable.
    if (swatch.getAttribute('aria-disabled') === 'true') return;

    const card = swatch.closest(CARD_SELECTOR);
    if (!card) return;

    markSelected(swatch);
    previewImage(card, swatch.dataset.mediaId, swatch.dataset.image);
    previewHoverImage(card, swatch.dataset.hoverImage, swatch.dataset.image);
    updateCardLinks(card, swatch.dataset.variantUrl || '');
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // "+N" — the colours that didn't fit. Opens the quick-add chooser (where
      // every colour is selectable) instead of navigating to the PDP.
      const more = target.closest(MORE_SELECTOR);
      if (more instanceof HTMLElement) {
        const card = more.closest(CARD_SELECTOR);
        // Must be the CHOOSE button specifically. A selector list returns whichever
        // matches first in the DOM — the ADD button — so "+N" would silently add.
        const choose = card?.querySelector('.quick-add__button--choose');

        // Only swallow the click if there IS a chooser to open; otherwise let the
        // element's own href take the shopper to the PDP as before.
        if (choose instanceof HTMLElement) {
          event.preventDefault();
          event.stopPropagation();
          choose.click();
        }
        return;
      }

      const swatch = target.closest(SWATCH_SELECTOR);
      if (!(swatch instanceof HTMLElement)) return;

      // The swatch sits inside (or over) the card's link — never let it navigate.
      event.preventDefault();
      event.stopPropagation();

      activateSwatch(swatch);
    },
    // Capture: the card link and the Meilisearch grid both listen for clicks, and
    // this must win before either can act on it.
    true
  );

  // The swatch is a role="button" span, not a native <button> (a wrapper element
  // that depends on new CSS collapsed the swatch in production), so Enter/Space
  // have to be wired up by hand to keep it keyboard-operable.
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;

      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.matches(`${SWATCH_SELECTOR}, ${MORE_SELECTOR}`)) return;

      event.preventDefault();
      target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    },
    true
  );
})();
